import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { Table } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Icon } from 'utils/icons';
import pluralize from 'pluralize';
import ColumnManager from './ColumnManager';
import ItemRow from './ItemRow';
import OvertimeHeader, { getOvertimeUnitLabel } from './OvertimeHeader';
import {
  expandColumnGroupOrder,
  getColumnInsertionIndex,
  getColumnGroupKey,
  getColumnGroupOrder,
  moveColumnKeyToIndex,
} from './columnDragUtils';

const COLUMN_MANAGER_TRIGGER_SELECTOR =
  '.detail-column-manager-trigger.is-active, .detail-column-manager-trigger';
const COLUMN_MANAGER_WIDTH = 320;
const COLUMN_MANAGER_VIEWPORT_MARGIN = 12;
const COLUMN_MANAGER_TRIGGER_GAP = 8;
const COLUMN_MANAGER_MIN_HEIGHT = 240;
const DETAIL_COLUMN_MIN_WIDTH = 48;
const DETAIL_COLUMN_MAX_WIDTH = 720;
const COLUMN_HEADER_INTERACTIVE_SELECTOR = [
  'button',
  'input',
  'select',
  'textarea',
  'a',
  '[role="button"]',
  '[data-column-header-control="true"]',
].join(',');

const clampNumber = (value, min, max) => Math.min(Math.max(value, min), max);

const parsePxWidth = width => {
  const parsed = Number.parseFloat(String(width || '').replace('px', ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeColumnsForLayout = columns => {
  const baseTableWidth = columns.reduce(
    (sum, column) => sum + parsePxWidth(column.width),
    0
  );

  if (!baseTableWidth) return columns;

  return columns.map(column => {
    const columnWidth = parsePxWidth(column.width);
    const proportionalWidth = `${(columnWidth / baseTableWidth) * 100}%`;

    return {
      ...column,
      width: proportionalWidth,
      minWidth: column.width,
    };
  });
};

const columnOrdersAreEqual = (left = [], right = []) =>
  left.length === right.length &&
  left.every((key, index) => key === right[index]);

const lockDragTransformToVerticalAxis = transform => {
  if (!transform) return transform;

  const translate3dMatch = transform.match(
    /translate3d\(([-\d.]+)px,\s*([-\d.]+)px,\s*([-\d.]+)px\)/
  );
  if (translate3dMatch) {
    return `translate3d(0px, ${translate3dMatch[2]}px, ${translate3dMatch[3]}px)`;
  }

  const translateMatch = transform.match(
    /translate\(([-\d.]+)px,\s*([-\d.]+)px\)/
  );
  if (translateMatch) {
    return `translate(0px, ${translateMatch[2]}px)`;
  }

  return transform;
};

const getColumnStyle = column => ({
  width: column.width,
  minWidth: column.minWidth,
  maxWidth: column.maxWidth,
  overflow: column.overflow,
});

const getColumnAccessibleLabel = column =>
  typeof column.label === 'string' ? column.label : column.key;

const isInteractiveColumnHeaderTarget = event => {
  const interactiveTarget = event.target?.closest?.(
    COLUMN_HEADER_INTERACTIVE_SELECTOR
  );

  return Boolean(
    interactiveTarget && event.currentTarget?.contains?.(interactiveTarget)
  );
};

const getRenderedColumnWidths = tableElement => {
  const headerCells = Array.from(
    tableElement?.querySelectorAll?.('thead th[data-detail-column-key]') || []
  );

  return headerCells.reduce((acc, cell) => {
    const columnKey = cell.dataset.detailColumnKey;
    const width = cell.getBoundingClientRect?.().width;

    if (columnKey && Number.isFinite(width)) {
      acc[columnKey] = Math.round(width);
    }

    return acc;
  }, {});
};

const getColumnGroupSelector = columnGroupKey =>
  `[data-detail-column-group-key="${String(columnGroupKey).replace(
    /"/g,
    '\\"'
  )}"]`;

const buildColumnDragImage = (tableElement, columnGroupKey) => {
  if (!tableElement || typeof document === 'undefined') return null;

  const groupSelector = getColumnGroupSelector(columnGroupKey);
  const columnCells = Array.from(tableElement.querySelectorAll(groupSelector));

  if (!columnCells.length) return null;

  const headerCells = Array.from(
    tableElement.querySelectorAll(`thead ${groupSelector}`)
  );
  const dragImage = document.createElement('div');
  dragImage.className = 'detail-column-drag-image';
  dragImage.style.width = `${headerCells.reduce(
    (width, cell) => width + cell.getBoundingClientRect().width,
    0
  )}px`;

  Array.from(tableElement.querySelectorAll('tr'))
    .map(row => Array.from(row.querySelectorAll(groupSelector)))
    .filter(cells => {
      const rect = cells[0]?.getBoundingClientRect();
      return rect && rect.bottom >= 0 && rect.top <= window.innerHeight;
    })
    .forEach((cells, index) => {
      const cellRects = cells.map(cell => cell.getBoundingClientRect());
      const dragCell = document.createElement('div');
      dragCell.className = [
        'detail-column-drag-image__cell',
        index === 0 ? 'is-header' : '',
      ]
        .filter(Boolean)
        .join(' ');
      dragCell.style.height = `${Math.max(
        ...cellRects.map(rect => rect.height)
      )}px`;
      dragCell.textContent = cells
        .map(cell => cell.innerText || cell.textContent || '')
        .join('   ');
      dragImage.appendChild(dragCell);
    });

  document.body.appendChild(dragImage);
  return dragImage;
};

const getColumnCellMeasurements = tableElement => {
  const cellIndexesByColumn = new Map();

  return Array.from(
    tableElement?.querySelectorAll?.('[data-detail-column-key]') || []
  ).map(cell => {
    const columnKey = cell.dataset.detailColumnKey;
    const cellIndex = cellIndexesByColumn.get(columnKey) || 0;
    cellIndexesByColumn.set(columnKey, cellIndex + 1);
    const rect = cell.getBoundingClientRect();

    return {
      columnKey,
      cellIndex,
      left: rect.left,
      top: rect.top,
    };
  });
};

const getHeaderRectsByColumn = tableElement =>
  Array.from(
    tableElement?.querySelectorAll?.('thead th[data-detail-column-key]') || []
  ).reduce((rectsByColumn, header) => {
    const rect = header.getBoundingClientRect();
    const groupKey =
      header.dataset.detailColumnGroupKey || header.dataset.detailColumnKey;
    const previousRect = rectsByColumn[groupKey];
    const left = Math.min(previousRect?.left ?? rect.left, rect.left);
    const right = Math.max(
      previousRect ? previousRect.left + previousRect.width : rect.right,
      rect.right
    );
    rectsByColumn[groupKey] = { left, width: right - left };
    return rectsByColumn;
  }, {});

const getColumnDragClassNames = ({ column, draggingColumnKey }) => {
  const classes = [];

  if (draggingColumnKey === getColumnGroupKey(column)) {
    classes.push('is-column-dragging');
  }

  return classes.join(' ');
};

const DetailsTableColGroup = ({ columns }) => (
  <colgroup>
    {columns.map(column => (
      <col key={column.key} style={getColumnStyle(column)} />
    ))}
  </colgroup>
);

DetailsTableColGroup.propTypes = {
  columns: PropTypes.array.isRequired,
};

const OvertimeRateHeader = ({ column, unitLabel }) => {
  const rateUnitLabel =
    unitLabel === 'Mixed Units'
      ? 'varies by unit'
      : `per ${pluralize.singular(unitLabel || 'unit')}`;

  return (
    <span className="overtime-rate-header">
      <span className="overtime-rate-header__title">{column.label}</span>
      <small className="overtime-rate-header__unit">{rateUnitLabel}</small>
    </span>
  );
};

OvertimeRateHeader.propTypes = {
  column: PropTypes.shape({ label: PropTypes.string.isRequired }).isRequired,
  unitLabel: PropTypes.string,
};

OvertimeRateHeader.defaultProps = {
  unitLabel: '',
};

const DetailsTableHeader = ({
  updatedColumns,
  hiddenColumn,
  draggingColumnKey,
  contextMenuColumnKey,
  resizingColumnKey,
  onColumnHeaderClick,
  onColumnHeaderContextMenu,
  onColumnHeaderPointerDown,
  onColumnResizeKeyDown,
  onColumnResizeStart,
  onUpdateOvertimeSettings,
  canEditOvertimeSettings,
  overtimeUnitLabel,
}) => (
  <thead>
    <tr>
      {updatedColumns?.map(column => (
        <th
          key={column.key}
          style={getColumnStyle(column)}
          className={[
            column.key,
            'detail-column-header',
            getColumnDragClassNames({
              column,
              draggingColumnKey,
            }),
            contextMenuColumnKey === getColumnGroupKey(column)
              ? 'has-column-context-menu'
              : '',
            resizingColumnKey === column.key ? 'is-column-resizing' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={event => onColumnHeaderClick(event, column)}
          onContextMenu={event => onColumnHeaderContextMenu(event, column)}
          onPointerDown={event => onColumnHeaderPointerDown(event, column)}
          data-detail-column-key={column.key}
          data-detail-column-group-key={getColumnGroupKey(column)}
        >
          <div>
            {(!hiddenColumn[column.key] || column.key === 'comment') &&
              (column.isOvertime ? (
                <OvertimeHeader
                  canEdit={canEditOvertimeSettings}
                  column={column}
                  onCommit={onUpdateOvertimeSettings}
                  unitLabel={overtimeUnitLabel}
                />
              ) : column.isOvertimeRate ? (
                <OvertimeRateHeader
                  column={column}
                  unitLabel={overtimeUnitLabel}
                />
              ) : (
                column.label
              ))}
            {column.icon}
          </div>
          <button
            aria-label={`Resize ${getColumnAccessibleLabel(column)} column`}
            className="detail-column-resize-handle"
            draggable={false}
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onKeyDown={event => onColumnResizeKeyDown(event, column)}
            onPointerDown={event => onColumnResizeStart(event, column)}
            title="Resize column"
            type="button"
          />
        </th>
      ))}
    </tr>
  </thead>
);

DetailsTableHeader.propTypes = {
  updatedColumns: PropTypes.array.isRequired,
  hiddenColumn: PropTypes.object.isRequired,
  draggingColumnKey: PropTypes.string,
  contextMenuColumnKey: PropTypes.string,
  resizingColumnKey: PropTypes.string,
  onColumnHeaderClick: PropTypes.func.isRequired,
  onColumnHeaderContextMenu: PropTypes.func.isRequired,
  onColumnHeaderPointerDown: PropTypes.func.isRequired,
  onColumnResizeKeyDown: PropTypes.func.isRequired,
  onColumnResizeStart: PropTypes.func.isRequired,
  onUpdateOvertimeSettings: PropTypes.func.isRequired,
  canEditOvertimeSettings: PropTypes.bool.isRequired,
  overtimeUnitLabel: PropTypes.string.isRequired,
};

DetailsTableHeader.defaultProps = {
  draggingColumnKey: null,
  contextMenuColumnKey: null,
  resizingColumnKey: null,
};

const DetailsTableFooter = ({
  columnCount,
  hasAppliedFringe,
  AppliedFringe,
  detailsTotal,
  usesExchangeRates,
  actualTotal,
  hasActualTotal,
  selectedBudget,
  formatCurrency,
}) => {
  const exchangeRateTitle =
    'This total is approximate because some detail line items are calculated using exchange rates that can fluctuate without warning.';

  return (
    <tfoot className="detailed-view-table-foot">
      <tr>
        <td colSpan={columnCount} style={{ textAlign: 'right' }}>
          {hasAppliedFringe && (
            <span className="totalCost" style={{ marginRight: '30px' }}>
              Fringe Total: {formatCurrency(AppliedFringe || 0)}
            </span>
          )}
          <span className="totalCost detail-total-with-indicator">
            Total:{' '}
            {formatCurrency(
              detailsTotal || 0,
              selectedBudget?.primary_currency
            )}
            {usesExchangeRates && (
              <span
                className="detail-total-exchange-indicator"
                title={exchangeRateTitle}
                aria-label={exchangeRateTitle}
              >
                <Icon name="InfoCircleIcon" />
              </span>
            )}
          </span>
          {hasActualTotal && (
            <span className="totalCost" style={{ marginLeft: '30px' }}>
              Actual Total:{' '}
              {formatCurrency(
                actualTotal || 0,
                selectedBudget?.primary_currency
              )}
            </span>
          )}
        </td>
      </tr>
    </tfoot>
  );
};

DetailsTableFooter.propTypes = {
  columnCount: PropTypes.number.isRequired,
  hasAppliedFringe: PropTypes.bool.isRequired,
  AppliedFringe: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  detailsTotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  usesExchangeRates: PropTypes.bool.isRequired,
  actualTotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  hasActualTotal: PropTypes.bool.isRequired,
  selectedBudget: PropTypes.object.isRequired,
  formatCurrency: PropTypes.func.isRequired,
};

const DetailsTable = ({
  updatedColumns,
  hiddenColumn,
  detailsRows,
  selectedItem,
  updatedRows,
  detailSheetType,
  addRow,
  onItemFieldCommit,
  handleKeyUp,
  focus,
  hasEditAccess,
  addEmptyRow,
  handleRowAction,
  focusEl,
  setFocusEl,
  fringeRowCount,
  checkedRows,
  selectedFringe,
  handleCheckboxChange,
  fringeRows,
  hoverData,
  highlightedRows,
  fringeGroups,
  hoveredFringeId,
  setHoveredFringeId,
  hoveredRowId,
  setHoveredRowId,
  onDragStart,
  onDragEnd,
  hasAppliedFringe,
  AppliedFringe,
  detailsTotal,
  usesExchangeRates,
  actualTotal,
  hasActualTotal,
  selectedBudget,
  formatCurrency,
  setShowModal,
  setSelectedFringe,
  refreshLinkedFringes,
  cellPresences,
  onCellPresenceFocus,
  onCellPresenceBlur,
  onDetailCellFocus,
  onClipboardCellFocus,
  commentCounts,
  onCommentCellFocus,
  onOpenCellComments,
  multiCellSelection,
  budgetGlobals,
  budgetTags,
  refetchBudgetTags,
  columnManagerColumns,
  isColumnManagerOpen,
  onColumnManagerOpenChange,
  onToggleColumn,
  onReorderColumns,
  onReorderColumnsByKey,
  onApplyColumnOrder,
  onResizeColumns,
  onResetColumns,
  onUpdateOvertimeSettings,
}) => {
  const shellRef = useRef(null);
  const tableRef = useRef(null);
  const suppressHeaderClickRef = useRef(false);
  const columnDragImageRef = useRef(null);
  const columnDragImageOffsetRef = useRef(0);
  const columnDragOrderRef = useRef(null);
  const columnDragStartOrderRef = useRef([]);
  const columnPointerDragRef = useRef(null);
  const columnDragFrameRef = useRef(null);
  const columnFlipRectsRef = useRef(null);
  const columnFlipFrameRef = useRef(null);
  const columnFlipTimeoutRef = useRef(null);
  const columnFlipElementsRef = useRef([]);
  const resizingColumnRef = useRef(null);
  const [dragRowWidth, setDragRowWidth] = useState(null);
  const [draggingColumnKey, setDraggingColumnKey] = useState(null);
  const [columnDragOrder, setColumnDragOrder] = useState(null);
  const [isColumnPointerDragging, setIsColumnPointerDragging] = useState(false);
  const [columnContextMenu, setColumnContextMenu] = useState(null);
  const [cellContextMenu, setCellContextMenu] = useState(null);
  const [columnManagerPosition, setColumnManagerPosition] = useState(null);
  const [resizingColumnKey, setResizingColumnKey] = useState(null);
  const cellPresenceMap = useMemo(() => {
    const map = new Map();

    cellPresences.forEach(presence => {
      const presenceCells =
        Array.isArray(presence.selected_cells) && presence.selected_cells.length
          ? presence.selected_cells
          : [
              {
                detail_sheet_type: presence.detail_sheet_type,
                row_id: presence.row_id,
                column_key: presence.column_key,
              },
            ];

      presenceCells.forEach(cell => {
        map.set(
          `${cell.detail_sheet_type || presence.detail_sheet_type}:${
            cell.row_id
          }:${cell.column_key}`,
          presence
        );
      });
    });

    return map;
  }, [cellPresences]);

  const visibleColumns = useMemo(
    () => (updatedColumns || []).filter(column => !hiddenColumn[column.key]),
    [updatedColumns, hiddenColumn]
  );

  const overtimeUnitLabel = useMemo(
    () => getOvertimeUnitLabel(detailsRows[selectedItem?.id]),
    [detailsRows, selectedItem?.id]
  );

  const previewVisibleColumns = useMemo(() => {
    if (!columnDragOrder) return visibleColumns;

    const columnsByKey = new Map(
      visibleColumns.map(column => [column.key, column])
    );
    const orderedColumns = columnDragOrder
      .map(columnKey => columnsByKey.get(columnKey))
      .filter(Boolean);
    const orderedColumnKeys = new Set(orderedColumns.map(column => column.key));

    visibleColumns.forEach(column => {
      if (!orderedColumnKeys.has(column.key)) {
        orderedColumns.push(column);
      }
    });

    return orderedColumns;
  }, [columnDragOrder, visibleColumns]);

  const layoutColumns = useMemo(
    () => normalizeColumnsForLayout(previewVisibleColumns),
    [previewVisibleColumns]
  );

  const columnManagerByKey = useMemo(
    () =>
      new Map(
        columnManagerColumns.map(column => [
          column.key,
          {
            label: column.label,
            locked: Boolean(column.locked),
          },
        ])
      ),
    [columnManagerColumns]
  );

  const getColumnMenuLabel = useCallback(
    column => {
      const managerColumn = columnManagerByKey.get(getColumnGroupKey(column));
      if (managerColumn?.label) return managerColumn.label;
      return typeof column.label === 'string' ? column.label : column.key;
    },
    [columnManagerByKey]
  );

  const updateColumnManagerPosition = useCallback(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const trigger = document.querySelector(COLUMN_MANAGER_TRIGGER_SELECTOR);
    const triggerRect = trigger?.getBoundingClientRect();
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const panelWidth = Math.min(
      COLUMN_MANAGER_WIDTH,
      Math.max(0, viewportWidth - COLUMN_MANAGER_VIEWPORT_MARGIN * 2)
    );
    const maxLeft = Math.max(
      COLUMN_MANAGER_VIEWPORT_MARGIN,
      viewportWidth - panelWidth - COLUMN_MANAGER_VIEWPORT_MARGIN
    );
    const desiredLeft = triggerRect ? triggerRect.right - panelWidth : maxLeft;
    const left = clampNumber(
      desiredLeft,
      COLUMN_MANAGER_VIEWPORT_MARGIN,
      maxLeft
    );
    const desiredTop = triggerRect
      ? triggerRect.bottom + COLUMN_MANAGER_TRIGGER_GAP
      : COLUMN_MANAGER_VIEWPORT_MARGIN;
    const top = Math.max(COLUMN_MANAGER_VIEWPORT_MARGIN, desiredTop);
    const maxHeight = Math.max(
      COLUMN_MANAGER_MIN_HEIGHT,
      viewportHeight - top - COLUMN_MANAGER_VIEWPORT_MARGIN
    );

    setColumnManagerPosition({
      left: `${left}px`,
      maxHeight: `${maxHeight}px`,
      right: 'auto',
      top: `${top}px`,
      width: `${panelWidth}px`,
    });
  }, []);

  useLayoutEffect(() => {
    if (!isColumnManagerOpen) {
      setColumnManagerPosition(null);
      return undefined;
    }

    updateColumnManagerPosition();
    window.addEventListener('resize', updateColumnManagerPosition);
    window.addEventListener('scroll', updateColumnManagerPosition, true);

    return () => {
      window.removeEventListener('resize', updateColumnManagerPosition);
      window.removeEventListener('scroll', updateColumnManagerPosition, true);
    };
  }, [isColumnManagerOpen, updateColumnManagerPosition]);

  const closeColumnContextMenu = useCallback(() => {
    setColumnContextMenu(null);
  }, []);

  const closeCellContextMenu = useCallback(() => {
    setCellContextMenu(null);
  }, []);

  const removeColumnDragImage = useCallback(() => {
    columnDragImageRef.current?.remove();
    columnDragImageRef.current = null;
    columnDragImageOffsetRef.current = 0;
  }, []);

  const updateColumnDragImagePosition = useCallback((clientX, top) => {
    const dragImage = columnDragImageRef.current;

    if (!dragImage || !Number.isFinite(clientX)) return;

    const imageRect = dragImage.getBoundingClientRect();
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth;
    const maxLeft = Math.max(0, viewportWidth - imageRect.width);
    const left = clampNumber(
      clientX - columnDragImageOffsetRef.current,
      0,
      maxLeft
    );

    dragImage.style.left = `${left}px`;
    if (Number.isFinite(top)) {
      dragImage.style.top = `${Math.max(0, top)}px`;
    }
  }, []);

  const clearColumnFlipAnimation = useCallback(() => {
    if (columnFlipFrameRef.current) {
      window.cancelAnimationFrame(columnFlipFrameRef.current);
      columnFlipFrameRef.current = null;
    }
    if (columnFlipTimeoutRef.current) {
      window.clearTimeout(columnFlipTimeoutRef.current);
      columnFlipTimeoutRef.current = null;
    }
    columnFlipElementsRef.current.forEach(cell => {
      cell.style.transform = '';
      cell.style.transition = '';
      cell.style.willChange = '';
    });
    columnFlipElementsRef.current = [];
    columnFlipRectsRef.current = null;
  }, []);

  const previewColumnOrder = useCallback(
    nextOrder => {
      if (columnOrdersAreEqual(columnDragOrderRef.current || [], nextOrder)) {
        return;
      }

      clearColumnFlipAnimation();
      columnFlipRectsRef.current = getColumnCellMeasurements(tableRef.current);
      columnDragOrderRef.current = nextOrder;
      setColumnDragOrder(nextOrder);
    },
    [clearColumnFlipAnimation]
  );

  useLayoutEffect(() => {
    const previousMeasurements = columnFlipRectsRef.current;
    if (!previousMeasurements?.length) return undefined;

    columnFlipRectsRef.current = null;
    const cellsByColumn = new Map();
    Array.from(
      tableRef.current?.querySelectorAll?.('[data-detail-column-key]') || []
    ).forEach(cell => {
      const columnKey = cell.dataset.detailColumnKey;
      const cells = cellsByColumn.get(columnKey) || [];
      cells.push(cell);
      cellsByColumn.set(columnKey, cells);
    });

    const animatedCells = previousMeasurements
      .map(({ columnKey, cellIndex, left }) => {
        const cell = cellsByColumn.get(columnKey)?.[cellIndex];
        if (!cell) return null;
        const distance = left - cell.getBoundingClientRect().left;
        if (Math.abs(distance) < 1) return null;

        cell.style.transition = 'none';
        cell.style.transform = `translateX(${distance}px)`;
        cell.style.willChange = 'transform';
        return cell;
      })
      .filter(Boolean);

    if (!animatedCells.length) return undefined;

    columnFlipElementsRef.current = animatedCells;
    columnFlipFrameRef.current = window.requestAnimationFrame(() => {
      animatedCells.forEach(cell => {
        cell.style.transition = 'transform 160ms cubic-bezier(0.2, 0, 0, 1)';
        cell.style.transform = 'translateX(0)';
      });
      columnFlipTimeoutRef.current = window.setTimeout(
        clearColumnFlipAnimation,
        180
      );
    });

    return () => {
      if (columnFlipFrameRef.current) {
        window.cancelAnimationFrame(columnFlipFrameRef.current);
        columnFlipFrameRef.current = null;
      }
    };
  }, [clearColumnFlipAnimation, columnDragOrder]);

  useEffect(
    () => () => {
      removeColumnDragImage();
      clearColumnFlipAnimation();
    },
    [clearColumnFlipAnimation, removeColumnDragImage]
  );

  useEffect(() => {
    if (!columnContextMenu) return undefined;

    const handleGlobalMouseDown = event => {
      if (event.target?.closest?.('.detail-column-context-menu')) return;
      closeColumnContextMenu();
    };
    const handleGlobalKeyDown = event => {
      if (event.key === 'Escape') closeColumnContextMenu();
    };

    window.addEventListener('mousedown', handleGlobalMouseDown);
    window.addEventListener('keydown', handleGlobalKeyDown);
    window.addEventListener('scroll', closeColumnContextMenu, true);

    return () => {
      window.removeEventListener('mousedown', handleGlobalMouseDown);
      window.removeEventListener('keydown', handleGlobalKeyDown);
      window.removeEventListener('scroll', closeColumnContextMenu, true);
    };
  }, [closeColumnContextMenu, columnContextMenu]);

  useEffect(() => {
    if (!cellContextMenu) return undefined;

    const handleGlobalMouseDown = event => {
      if (event.target?.closest?.('.detail-cell-context-menu')) return;
      closeCellContextMenu();
    };
    const handleGlobalKeyDown = event => {
      if (event.key === 'Escape') closeCellContextMenu();
    };

    window.addEventListener('mousedown', handleGlobalMouseDown);
    window.addEventListener('keydown', handleGlobalKeyDown);
    window.addEventListener('scroll', closeCellContextMenu, true);

    return () => {
      window.removeEventListener('mousedown', handleGlobalMouseDown);
      window.removeEventListener('keydown', handleGlobalKeyDown);
      window.removeEventListener('scroll', closeCellContextMenu, true);
    };
  }, [cellContextMenu, closeCellContextMenu]);

  const suppressNextHeaderClick = useCallback(() => {
    suppressHeaderClickRef.current = true;
    window.setTimeout(() => {
      suppressHeaderClickRef.current = false;
    }, 0);
  }, []);

  const handleColumnHeaderClick = useCallback((event, column) => {
    if (
      suppressHeaderClickRef.current ||
      isInteractiveColumnHeaderTarget(event)
    ) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    column.onClick?.(event);
  }, []);

  const handleColumnResizeStart = useCallback(
    (event, column) => {
      if (event.button !== undefined && event.button !== 0) return;

      event.preventDefault();
      event.stopPropagation();

      const renderedWidths = getRenderedColumnWidths(tableRef.current);
      const startWidth =
        renderedWidths[column.key] ||
        parsePxWidth(column.width) ||
        DETAIL_COLUMN_MIN_WIDTH;

      resizingColumnRef.current = {
        columnKey: column.key,
        pointerId: event.pointerId,
        renderedWidths,
        startWidth,
        startX: event.clientX,
      };

      closeColumnContextMenu();
      setResizingColumnKey(column.key);
    },
    [closeColumnContextMenu]
  );

  const handleColumnResizeKeyDown = useCallback(
    (event, column) => {
      const direction =
        event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0;

      if (!direction) return;

      event.preventDefault();
      event.stopPropagation();

      const renderedWidths = getRenderedColumnWidths(tableRef.current);
      const currentWidth =
        renderedWidths[column.key] ||
        parsePxWidth(column.width) ||
        DETAIL_COLUMN_MIN_WIDTH;

      onResizeColumns({
        ...renderedWidths,
        [column.key]: clampNumber(
          currentWidth + direction * 16,
          DETAIL_COLUMN_MIN_WIDTH,
          DETAIL_COLUMN_MAX_WIDTH
        ),
      });
      closeColumnContextMenu();
      suppressNextHeaderClick();
    },
    [closeColumnContextMenu, onResizeColumns, suppressNextHeaderClick]
  );

  useEffect(() => {
    if (
      !resizingColumnKey ||
      typeof window === 'undefined' ||
      typeof document === 'undefined'
    ) {
      return undefined;
    }

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handlePointerMove = event => {
      const resizeState = resizingColumnRef.current;

      if (!resizeState) return;
      if (
        resizeState.pointerId !== undefined &&
        event.pointerId !== resizeState.pointerId
      ) {
        return;
      }

      event.preventDefault();

      const nextWidth = clampNumber(
        resizeState.startWidth + event.clientX - resizeState.startX,
        DETAIL_COLUMN_MIN_WIDTH,
        DETAIL_COLUMN_MAX_WIDTH
      );

      if (resizeState.lastWidth === nextWidth) return;

      resizeState.lastWidth = nextWidth;
      onResizeColumns({
        ...resizeState.renderedWidths,
        [resizeState.columnKey]: nextWidth,
      });
    };

    const handlePointerEnd = event => {
      const resizeState = resizingColumnRef.current;

      if (
        resizeState?.pointerId !== undefined &&
        event.pointerId !== resizeState.pointerId
      ) {
        return;
      }

      resizingColumnRef.current = null;
      setResizingColumnKey(null);
      suppressNextHeaderClick();
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerEnd);
    window.addEventListener('pointercancel', handlePointerEnd);

    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerEnd);
      window.removeEventListener('pointercancel', handlePointerEnd);
    };
  }, [onResizeColumns, resizingColumnKey, suppressNextHeaderClick]);

  const handleColumnHeaderContextMenu = useCallback(
    (event, column) => {
      if (isInteractiveColumnHeaderTarget(event)) return;

      event.preventDefault();
      event.stopPropagation();

      const shellRect = shellRef.current?.getBoundingClientRect();
      const columnGroupKey = getColumnGroupKey(column);
      const managerColumn = columnManagerByKey.get(columnGroupKey);

      setColumnContextMenu({
        key: columnGroupKey,
        label: getColumnMenuLabel(column),
        locked: Boolean(managerColumn?.locked),
        x: shellRect ? event.clientX - shellRect.left : event.clientX,
        y: shellRect ? event.clientY - shellRect.top : event.clientY,
      });
    },
    [columnManagerByKey, getColumnMenuLabel]
  );

  const handleCellCommentContextMenu = useCallback(({ event, target }) => {
    if (!target) return;
    setCellContextMenu({
      target,
      x: event.clientX,
      y: event.clientY,
    });
  }, []);

  const getCommentTargetFromEvent = useCallback(
    event => {
      const cell = event.target?.closest?.(
        'td[data-detail-row-id][data-detail-column-key]'
      );
      // This handler already runs on the table in capture phase. Avoid relying
      // on React Bootstrap's forwarded ref, which is not consistently a DOM
      // element across builds and can otherwise reject every valid cell.
      if (!cell) return null;
      if (
        cell.dataset.detailRowDraft === 'true' ||
        cell.dataset.detailCommentDisabled === 'true' ||
        event.target?.closest?.('.dropdown, button, [role="button"]')
      ) {
        return null;
      }

      const rowId = Number(cell.dataset.detailRowId);
      const columnKey = cell.dataset.detailColumnKey;
      if (!Number.isInteger(rowId) || rowId <= 0 || columnKey === 'checkbox') {
        return null;
      }

      const rect = cell.getBoundingClientRect();
      return {
        detailSheetType,
        rowId,
        columnKey,
        anchorRect: {
          bottom: rect.bottom,
          left: rect.left,
          right: rect.right,
          top: rect.top,
        },
      };
    },
    [detailSheetType]
  );

  const handleCellCommentMouseDownCapture = useCallback(
    event => {
      if (event.button !== 0) return;
      const target = getCommentTargetFromEvent(event);
      if (target) onCommentCellFocus(target);
    },
    [getCommentTargetFromEvent, onCommentCellFocus]
  );

  const handleCellCommentContextMenuCapture = useCallback(
    event => {
      const target = getCommentTargetFromEvent(event);
      if (!target) return;

      event.preventDefault();
      event.stopPropagation();
      if (multiCellSelection?.selectedCellKeys?.size > 1) return;

      onCommentCellFocus(target);
      handleCellCommentContextMenu({ event, target });
    },
    [
      getCommentTargetFromEvent,
      handleCellCommentContextMenu,
      multiCellSelection?.selectedCellKeys?.size,
      onCommentCellFocus,
    ]
  );

  const handleOpenContextCellComments = useCallback(() => {
    if (!cellContextMenu?.target) return;
    onOpenCellComments(cellContextMenu.target);
    closeCellContextMenu();
  }, [cellContextMenu, closeCellContextMenu, onOpenCellComments]);

  const handleColumnHeaderPointerDown = useCallback(
    (event, column) => {
      if (event.button !== 0 || isInteractiveColumnHeaderTarget(event)) return;

      const startingColumnOrder = getColumnGroupOrder(visibleColumns);
      const headerRect = event.currentTarget.getBoundingClientRect();
      columnPointerDragRef.current = {
        headerRectsByKey: getHeaderRectsByColumn(tableRef.current),
        pointerId: event.pointerId,
        sourceKey: getColumnGroupKey(column),
        startOrder: startingColumnOrder,
        startPhysicalOrder: visibleColumns.map(
          visibleColumn => visibleColumn.key
        ),
        startX: event.clientX,
        startY: event.clientY,
        grabOffsetX: event.clientX - headerRect.left,
        top: headerRect.top,
        status: 'pending',
      };
      event.currentTarget.setPointerCapture?.(event.pointerId);
      closeColumnContextMenu();
    },
    [closeColumnContextMenu, visibleColumns]
  );

  const commitColumnDragOrder = useCallback(
    sourceKey => {
      const finalOrder = columnDragOrderRef.current;
      const startingOrder = columnDragStartOrderRef.current;

      if (
        !sourceKey ||
        !Array.isArray(finalOrder) ||
        columnOrdersAreEqual(finalOrder, startingOrder)
      ) {
        return;
      }

      const columnsByKey = new Map(
        visibleColumns.map(column => [column.key, column])
      );
      const finalGroupOrder = getColumnGroupOrder(
        finalOrder.map(columnKey => columnsByKey.get(columnKey)).filter(Boolean)
      );
      const sourceIndex = finalGroupOrder.indexOf(sourceKey);

      if (sourceIndex === -1) return;

      const nextColumnKey = finalGroupOrder[sourceIndex + 1];
      const previousColumnKey = finalGroupOrder[sourceIndex - 1];

      if (nextColumnKey) {
        onReorderColumnsByKey(sourceKey, nextColumnKey, false);
      } else if (previousColumnKey) {
        onReorderColumnsByKey(sourceKey, previousColumnKey, true);
      }
    },
    [onReorderColumnsByKey, visibleColumns]
  );

  const clearColumnPointerDrag = useCallback(() => {
    const drag = columnPointerDragRef.current;
    if (columnDragFrameRef.current) {
      window.cancelAnimationFrame(columnDragFrameRef.current);
      columnDragFrameRef.current = null;
    }
    if (drag?.status === 'active') {
      document.body.style.cursor = drag.previousCursor;
      document.body.style.userSelect = drag.previousUserSelect;
    }
    columnPointerDragRef.current = null;
    setIsColumnPointerDragging(false);
    setDraggingColumnKey(null);
    setColumnDragOrder(null);
    columnDragOrderRef.current = null;
    columnDragStartOrderRef.current = [];
    removeColumnDragImage();
  }, [removeColumnDragImage]);

  useEffect(() => {
    const updatePreview = drag => {
      const targetIndex = getColumnInsertionIndex(
        drag.startOrder,
        drag.sourceKey,
        drag.headerRectsByKey,
        drag.lastClientX
      );
      const nextGroupOrder = moveColumnKeyToIndex(
        drag.startOrder,
        drag.sourceKey,
        targetIndex
      );
      previewColumnOrder(
        expandColumnGroupOrder(nextGroupOrder, visibleColumns).map(
          column => column.key
        )
      );
    };

    const handlePointerMove = event => {
      const drag = columnPointerDragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;

      const movedFarEnough =
        Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) >
        5;
      if (drag.status === 'pending') {
        if (!movedFarEnough) return;

        removeColumnDragImage();
        const dragImage = buildColumnDragImage(
          tableRef.current,
          drag.sourceKey
        );
        drag.status = 'active';
        drag.previousCursor = document.body.style.cursor;
        drag.previousUserSelect = document.body.style.userSelect;
        document.body.style.cursor = 'grabbing';
        document.body.style.userSelect = 'none';
        columnDragOrderRef.current = drag.startPhysicalOrder;
        columnDragStartOrderRef.current = drag.startPhysicalOrder;
        setDraggingColumnKey(drag.sourceKey);
        setColumnDragOrder(drag.startPhysicalOrder);
        setIsColumnPointerDragging(true);

        if (dragImage) {
          columnDragImageRef.current = dragImage;
          columnDragImageOffsetRef.current = drag.grabOffsetX;
          updateColumnDragImagePosition(event.clientX, drag.top);
        }
      }

      if (drag.status !== 'active') return;
      event.preventDefault();
      drag.lastClientX = event.clientX;
      updateColumnDragImagePosition(event.clientX, drag.top);

      if (columnDragFrameRef.current) return;
      columnDragFrameRef.current = window.requestAnimationFrame(() => {
        columnDragFrameRef.current = null;
        const activeDrag = columnPointerDragRef.current;
        if (activeDrag?.status === 'active') updatePreview(activeDrag);
      });
    };

    const finishPointerDrag = (event, shouldCommit) => {
      const drag = columnPointerDragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;

      if (drag.status === 'active') {
        if (columnDragFrameRef.current) {
          window.cancelAnimationFrame(columnDragFrameRef.current);
          columnDragFrameRef.current = null;
        }
        drag.lastClientX = event.clientX;
        updatePreview(drag);
        if (shouldCommit) commitColumnDragOrder(drag.sourceKey);
        suppressNextHeaderClick();
      }

      clearColumnPointerDrag();
    };

    const handlePointerUp = event => finishPointerDrag(event, true);
    const handlePointerCancel = event => finishPointerDrag(event, false);

    window.addEventListener('pointermove', handlePointerMove, {
      passive: false,
    });
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerCancel);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerCancel);
    };
  }, [
    clearColumnPointerDrag,
    commitColumnDragOrder,
    previewColumnOrder,
    removeColumnDragImage,
    suppressNextHeaderClick,
    updateColumnDragImagePosition,
    visibleColumns,
  ]);

  const handleHideColumnFromContextMenu = useCallback(() => {
    if (!columnContextMenu || columnContextMenu.locked) return;

    onToggleColumn(columnContextMenu.key);
    closeColumnContextMenu();
  }, [closeColumnContextMenu, columnContextMenu, onToggleColumn]);

  const handleColumnManagerPreviewOrder = useCallback(
    columnKeys => {
      if (!Array.isArray(columnKeys) || columnKeys.length === 0) {
        setColumnDragOrder(null);
        columnDragOrderRef.current = null;
        return;
      }

      const physicalColumnOrder = expandColumnGroupOrder(
        columnKeys,
        visibleColumns
      ).map(column => column.key);
      setColumnDragOrder(physicalColumnOrder);
      columnDragOrderRef.current = physicalColumnOrder;
    },
    [visibleColumns]
  );

  const handleColumnManagerClearPreview = useCallback(() => {
    setColumnDragOrder(null);
    columnDragOrderRef.current = null;
  }, []);

  const handleDragStart = useCallback(
    (...args) => {
      const tableWidth = tableRef.current?.getBoundingClientRect?.().width;
      setDragRowWidth(tableWidth || null);
      onDragStart(...args);
    },
    [onDragStart]
  );

  const handleDragEnd = useCallback(
    result => {
      setDragRowWidth(null);
      onDragEnd(result);
    },
    [onDragEnd]
  );

  const getDraggableRowStyle = useCallback(
    (draggableStyle, isDragging) => {
      if (!isDragging) return draggableStyle;

      return {
        ...draggableStyle,
        display: 'table',
        tableLayout: 'fixed',
        width: dragRowWidth ? `${dragRowWidth}px` : draggableStyle?.width,
        transform: lockDragTransformToVerticalAxis(draggableStyle?.transform),
      };
    },
    [dragRowWidth]
  );

  const columnDragState = useMemo(
    () => ({
      draggingColumnKey,
    }),
    [draggingColumnKey]
  );

  return (
    <div className="detailed-view-shell" ref={shellRef}>
      {isColumnManagerOpen && (
        <ColumnManager
          columns={columnManagerColumns}
          onClose={() => onColumnManagerOpenChange(false)}
          onDone={() => onColumnManagerOpenChange(false)}
          onReset={onResetColumns}
          onToggleColumn={onToggleColumn}
          onReorderColumns={onReorderColumns}
          onReorderColumnsByKey={onReorderColumnsByKey}
          onApplyColumnOrder={onApplyColumnOrder}
          onPreviewColumnOrder={handleColumnManagerPreviewOrder}
          onClearColumnPreview={handleColumnManagerClearPreview}
          style={columnManagerPosition || { visibility: 'hidden' }}
        />
      )}
      {columnContextMenu && (
        <div
          className="detail-column-context-menu"
          style={{
            left: columnContextMenu.x,
            top: columnContextMenu.y,
          }}
          role="menu"
          onMouseDown={event => event.stopPropagation()}
        >
          <button
            type="button"
            className="detail-column-context-menu__item"
            disabled={columnContextMenu.locked}
            role="menuitem"
            onClick={handleHideColumnFromContextMenu}
          >
            {columnContextMenu.locked
              ? `${columnContextMenu.label} column is required`
              : `Hide ${columnContextMenu.label}`}
          </button>
        </div>
      )}
      {cellContextMenu && (
        <div
          className="detail-cell-context-menu"
          role="menu"
          style={{
            left: cellContextMenu.x,
            top: cellContextMenu.y,
          }}
          onMouseDown={event => event.stopPropagation()}
        >
          <button
            onClick={handleOpenContextCellComments}
            role="menuitem"
            type="button"
          >
            Add Comment
          </button>
        </div>
      )}

      <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
        <Table
          striped
          bordered
          hover
          className={[
            'detailed-view-table',
            isColumnPointerDragging ? 'is-column-reordering' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onContextMenuCapture={handleCellCommentContextMenuCapture}
          onMouseDownCapture={handleCellCommentMouseDownCapture}
          ref={tableRef}
        >
          <DetailsTableColGroup columns={layoutColumns} />
          <DetailsTableHeader
            updatedColumns={layoutColumns}
            hiddenColumn={hiddenColumn}
            draggingColumnKey={draggingColumnKey}
            contextMenuColumnKey={columnContextMenu?.key}
            resizingColumnKey={resizingColumnKey}
            onColumnHeaderClick={handleColumnHeaderClick}
            onColumnHeaderContextMenu={handleColumnHeaderContextMenu}
            onColumnHeaderPointerDown={handleColumnHeaderPointerDown}
            onColumnResizeKeyDown={handleColumnResizeKeyDown}
            onColumnResizeStart={handleColumnResizeStart}
            onUpdateOvertimeSettings={onUpdateOvertimeSettings}
            canEditOvertimeSettings={hasEditAccess()}
            overtimeUnitLabel={overtimeUnitLabel}
          />
          <Droppable droppableId="droppable">
            {provided => (
              <tbody {...provided.droppableProps} ref={provided.innerRef}>
                {detailsRows[selectedItem?.id]
                  // Do not show empty rows ("Enter item...") to users who cannot edit the budget
                  ?.filter(rowData => rowData?.id || hasEditAccess())
                  ?.map((rowData, index) => {
                    const stableRowKey = rowData.row_id || rowData.id;
                    return (
                      <Draggable
                        key={stableRowKey}
                        draggableId={stableRowKey?.toString()}
                        index={index}
                        isDragDisabled={!rowData?.id || !hasEditAccess()}
                      >
                        {(provide, snapshot) => (
                          <tr
                            ref={provide.innerRef}
                            {...provide.draggableProps}
                            {...provide.dragHandleProps}
                            className={[
                              highlightedRows.some(
                                highlight => highlight.id === rowData.id
                              )
                                ? 'highlighted-row'
                                : '',
                              snapshot.isDragging
                                ? 'detailed-view-table__row--dragging'
                                : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            style={getDraggableRowStyle(
                              provide.draggableProps.style,
                              snapshot.isDragging
                            )}
                          >
                            <ItemRow
                              setFringeGroups={
                                fringeGroups?.setter || (() => undefined)
                              }
                              key={stableRowKey}
                              columns={layoutColumns}
                              rowData={rowData}
                              selectedItem={selectedItem}
                              detailSheetType={detailSheetType}
                              refreshLinkedFringes={refreshLinkedFringes}
                              allRows={updatedRows}
                              addRow={addRow}
                              onItemFieldCommit={onItemFieldCommit}
                              onKeyUp={handleKeyUp}
                              focus={focus}
                              index={index}
                              lastIndex={updatedRows.length - 1}
                              hiddenColumn={hiddenColumn}
                              canEdit={hasEditAccess()}
                              addEmptyRow={addEmptyRow}
                              handleRowAction={handleRowAction}
                              hideOptions={Boolean(
                                index + 1 === updatedRows.length && !rowData?.id
                              )}
                              focusEl={focusEl}
                              setFocusEl={setFocusEl}
                              fringeRowCount={fringeRowCount}
                              isChecked={
                                checkedRows[selectedFringe?.id]?.[rowData.id] ||
                                false
                              }
                              onCheckboxChange={handleCheckboxChange(
                                selectedFringe?.id,
                                rowData.id
                              )}
                              setShowModal={setShowModal}
                              setSelectedFringe={setSelectedFringe}
                              fringeRows={fringeRows}
                              selectedFringe={selectedFringe}
                              hoverData={hoverData}
                              highlightedRows={highlightedRows}
                              fringeGroups={fringeGroups}
                              hoveredFringeId={hoveredFringeId}
                              setHoveredFringeId={setHoveredFringeId}
                              hoveredRowId={hoveredRowId}
                              setHoveredRowId={setHoveredRowId}
                              cellPresenceMap={cellPresenceMap}
                              onCellPresenceFocus={onCellPresenceFocus}
                              onCellPresenceBlur={onCellPresenceBlur}
                              onDetailCellFocus={onDetailCellFocus}
                              onClipboardCellFocus={onClipboardCellFocus}
                              commentCounts={commentCounts}
                              onCommentCellFocus={onCommentCellFocus}
                              onCellCommentContextMenu={
                                handleCellCommentContextMenu
                              }
                              onOpenCellComments={onOpenCellComments}
                              multiCellSelection={multiCellSelection}
                              budgetGlobals={budgetGlobals}
                              budgetTags={budgetTags}
                              refetchBudgetTags={refetchBudgetTags}
                              columnDragState={columnDragState}
                            />
                          </tr>
                        )}
                      </Draggable>
                    );
                  })}
                {provided.placeholder}
              </tbody>
            )}
          </Droppable>
          <DetailsTableFooter
            columnCount={layoutColumns.length}
            hasAppliedFringe={hasAppliedFringe}
            AppliedFringe={AppliedFringe}
            detailsTotal={detailsTotal}
            usesExchangeRates={usesExchangeRates}
            actualTotal={actualTotal}
            hasActualTotal={hasActualTotal}
            selectedBudget={selectedBudget}
            formatCurrency={formatCurrency}
          />
        </Table>
      </DragDropContext>
    </div>
  );
};

DetailsTable.propTypes = {
  updatedColumns: PropTypes.array.isRequired,
  hiddenColumn: PropTypes.object.isRequired,
  detailsRows: PropTypes.object.isRequired,
  selectedItem: PropTypes.object.isRequired,
  detailSheetType: PropTypes.string.isRequired,
  updatedRows: PropTypes.array.isRequired,
  addRow: PropTypes.func.isRequired,
  onItemFieldCommit: PropTypes.func.isRequired,
  handleKeyUp: PropTypes.func.isRequired,
  focus: PropTypes.bool.isRequired,
  hasEditAccess: PropTypes.func.isRequired,
  addEmptyRow: PropTypes.func.isRequired,
  handleRowAction: PropTypes.func.isRequired,
  focusEl: PropTypes.any,
  setFocusEl: PropTypes.func.isRequired,
  fringeRowCount: PropTypes.object.isRequired,
  checkedRows: PropTypes.object.isRequired,
  selectedFringe: PropTypes.object,
  handleCheckboxChange: PropTypes.func.isRequired,
  fringeRows: PropTypes.object.isRequired,
  hoverData: PropTypes.any,
  highlightedRows: PropTypes.array.isRequired,
  fringeGroups: PropTypes.object.isRequired,
  hoveredFringeId: PropTypes.any,
  setHoveredFringeId: PropTypes.func.isRequired,
  hoveredRowId: PropTypes.any,
  setHoveredRowId: PropTypes.func.isRequired,
  onDragStart: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  hasFringeTotalColumn: PropTypes.bool.isRequired,
  hasAppliedFringe: PropTypes.bool.isRequired,
  AppliedFringe: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  detailsTotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  usesExchangeRates: PropTypes.bool.isRequired,
  actualTotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  hasActualTotal: PropTypes.bool.isRequired,
  selectedBudget: PropTypes.object.isRequired,
  formatCurrency: PropTypes.func.isRequired,
  setShowModal: PropTypes.func.isRequired,
  setSelectedFringe: PropTypes.func.isRequired,
  refreshLinkedFringes: PropTypes.func.isRequired,
  cellPresences: PropTypes.array,
  onCellPresenceFocus: PropTypes.func,
  onCellPresenceBlur: PropTypes.func,
  onDetailCellFocus: PropTypes.func,
  onClipboardCellFocus: PropTypes.func.isRequired,
  commentCounts: PropTypes.instanceOf(Map),
  onCommentCellFocus: PropTypes.func,
  onOpenCellComments: PropTypes.func.isRequired,
  multiCellSelection: PropTypes.object.isRequired,
  budgetGlobals: PropTypes.array,
  budgetTags: PropTypes.array,
  refetchBudgetTags: PropTypes.func,
  columnManagerColumns: PropTypes.array.isRequired,
  isColumnManagerOpen: PropTypes.bool.isRequired,
  onColumnManagerOpenChange: PropTypes.func.isRequired,
  onToggleColumn: PropTypes.func.isRequired,
  onReorderColumns: PropTypes.func.isRequired,
  onReorderColumnsByKey: PropTypes.func.isRequired,
  onApplyColumnOrder: PropTypes.func.isRequired,
  onResizeColumns: PropTypes.func.isRequired,
  onResetColumns: PropTypes.func.isRequired,
  onUpdateOvertimeSettings: PropTypes.func,
};

DetailsTable.defaultProps = {
  focusEl: null,
  selectedFringe: null,
  hoverData: null,
  hoveredFringeId: null,
  hoveredRowId: null,
  cellPresences: [],
  onCellPresenceFocus: () => null,
  onCellPresenceBlur: () => null,
  onDetailCellFocus: () => null,
  commentCounts: new Map(),
  onCommentCellFocus: () => null,
  budgetGlobals: [],
  budgetTags: [],
  refetchBudgetTags: () => null,
  onUpdateOvertimeSettings: () => null,
};

export default DetailsTable;
